/**
 * BackButton — IconButton with the iOS chevron-back glyph.
 *
 * iOS source of truth: client-native/src/ui/molecules/BackButton.tsx →
 * `IconButton name="chevron-back"`, which renders Ionicons `chevron-back` — the
 * rounded, slightly bolder iOS-system back chevron used on EVERY iOS screen.
 *
 * We previously used lucide `ChevronLeft`, a thinner/generic chevron that read
 * as off next to the rest of the app. Render the exact Ionicons `chevron-back`
 * SVG path instead so the web back affordance matches iOS pixel-for-pixel
 * everywhere BackButton is used. On iOS the default onPress is router.back();
 * here it's left to the caller (no router coupling at the DS level).
 */
import { FC } from "react";
import IconButton from "./IconButton";

export interface BackButtonProps {
  onPress?: () => void;
  color?: string;
  /** Visual glyph size in px. Defaults to 24 (iOS back chevron). */
  size?: number;
}

/**
 * Ionicons `chevron-back` glyph — verbatim path (viewBox 512, stroke-width 48).
 * Exported so non-BackButton back affordances (e.g. a circular overlay button
 * that can't nest BackButton's own <button>) render the SAME iOS chevron.
 */
export const ChevronBackGlyph: FC<{ size: number; color: string }> = ({
  size,
  color,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 512 512"
    aria-hidden
    style={{ display: "block" }}
  >
    <path
      d="M328 112L184 256l144 144"
      fill="none"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={48}
    />
  </svg>
);

const BackButton: FC<BackButtonProps> = ({
  onPress,
  color = "var(--color-text-primary)",
  size = 24,
}) => (
  <IconButton
    icon={<ChevronBackGlyph size={size} color={color} />}
    color={color}
    onPress={onPress}
    ariaLabel="Go back"
  />
);

export default BackButton;
